import React from "react";
import { isObject, isUndefined, isNil, isEmpty, isString } from "lodash";
import { CZ_COUNTRY_CODE } from "./constants";

export const oneOf = (...args) => args.some((a) => a === true);

export const isCountryCz = (country) =>
  isObject(country) && country.code === CZ_COUNTRY_CODE;

export const compareArrayLength = (array1, array2) =>
  !isUndefined(array1) &&
  !isUndefined(array2) &&
  array1.length !== array2.length;

export const fieldsRemove =
  (fields, index, min = 0, obj = {}) =>
  () => {
    if (fields.length <= min) {
      fields.push(obj);
    }
    fields.remove(index);
  };

export const countryChange =
  (change, municipalityField, oldCountry) => (country) => {
    if (!isObject(country)) {
      change(municipalityField, "");
      return;
    }

    if (isObject(oldCountry) && oldCountry.code !== country.code) {
      change(municipalityField, "");
    }
  };

const daysBetween = (date1, date2) => {
  const oneDay = 24 * 60 * 60 * 1000; // hours * minutes * seconds * milliseconds
  return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
};

export const displayHowManyDaysLeft = (date, shouldDisplay) => {
  const days = daysBetween(new Date(), new Date(date));
  const daysLeft = 30 - days;
  return (
    shouldDisplay &&
    (daysLeft > 0 ? (
      <div>{daysLeft}</div>
    ) : (
      <div style={{ color: "red" }}>0</div>
    ))
  );
};

export const isBeforeOrAtDay = (date1, date2) =>
  Math.round(
    (new Date(date1).getTime() - new Date(date2).getTime()) /
      (24 * 60 * 60 * 1000),
  ) <= 0;

export const showProp = (field, nameOfProp) => {
  let prop = field && field[0] ? field[0][nameOfProp] : "";
  if (field.length > 1) {
    return prop + " (+" + (field.length - 1) + ")";
  }
  return prop;
};

const isInRole = (auth, role) => !isNil(auth) && auth.role === role;
const hasRoleInMore = (auth, role) => !isNil(auth) && auth.more.includes(role);
export const isInternal = (auth) => isInRole(auth, "ROLE_INTERNAL");
export const isOvm = (auth) => isInRole(auth, "ROLE_OVM");
export const isLegitimate = (auth) => isInRole(auth, "ROLE_LEGITIMATE");
export const isOfficial = (auth) => isInRole(auth, "ROLE_OFFICIAL");
export const isPublic = (auth) => isInRole(auth, "ROLE_PUBLIC");
export const isNotLogged = (auth) => isNil(auth) || isNil(auth.role);
export const isLogged = (auth) => !isNotLogged(auth);
export const isOfficialAdmin = (auth) =>
  isInRole(auth, "ROLE_INTERNAL") &&
  hasRoleInMore(auth, "ROLE_INTERNAL_OFFICIAL_ADMIN");
export const isNotLoggedOrIsPublic = (auth) =>
  isPublic(auth) || isNotLogged(auth);
export const isAdmin = (auth) =>
  isInRole(auth, "ROLE_INTERNAL") && hasRoleInMore(auth, "ROLE_INTERNAL_ADMIN");

export const publicUrlHelp = (auth) =>
  isPublic(auth) ? "/verejnost-prihlasen" : "/verejnost";
export const publicUrlHelpLogged = (auth) =>
  isPublic(auth) ? "/verejnost-prihlasen" : auth.startUrl;

export const showDegrees = (before, after) => {
  return [before, after].filter((s) => !isEmpty(s)).join(", ");
};

export const checkWorkingPositions = (workingPositions, position) => {
  return workingPositions.some((wp) =>
    wp.workingPosition.name.includes(position),
  );
};

//normalize functions

export const exportNumWithDot = (value, previousValue) => {
  if (!value) return "";
  //return /^[0-9]+[.,]?[0-9]*$/i.test(value) ? value.toString().replace(',', '.') : previousValue;
  return value.toString().replace(/ /g, "").replace(",", ".");
};

export const importNumWithComma = (value) => {
  if (value === 0) return "0";
  if (!value) return "";
  return value.toString().replace(".", ",");
};

export const normalizeInteger = (value, previousValue) => {
  if (!value) return "";
  return /[0-9]+?$/i.test(value) ? value : previousValue;
};

export const maxLength = (maxlen) => (value, previousValue) => {
  if (isString(value)) {
    return value.length <= maxlen ? value : previousValue;
  }
  return value;
};

export const formatCurrency = (value, currencySymbol = 'Kč', minFractionDigits = 2, maxFractionDigits = 2) => {
  if (isNil(value) || value === "") return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString("cs-CZ", { minimumFractionDigits: minFractionDigits, maximumFractionDigits: maxFractionDigits }) + ' ' + currencySymbol;
}

export const copySubstatement = (
  toName,
  from,
  fromName,
  toEnabledName,
  fromEnabled,
  fromEnabledName,
  change,
) => {
  if (!isEmpty(from)) {
    change(toName, from);
    change(fromName, null);
  }
  if (!isNil(fromEnabled)) {
    change(toEnabledName, fromEnabled);
    change(fromEnabledName, false);
  }
};

export const notForPublic = (isPublicStatement, auth) =>
  (!isPublicStatement && isNotLoggedOrIsPublic(auth)) ||
  !isNotLoggedOrIsPublic(auth);

export const dictToOptions = (dict) =>
  dict.map((item) => ({ id: item.id, label: item.name }));

export const decodeBase64 = (str) => {
  try {
    return decodeURIComponent(escape(window.atob(str)));
  } catch (e) {
    return str;
  }
};

export const encodeBase64 = (str) => {
  try {
    return window.btoa(unescape(encodeURIComponent(str)));
  } catch (e) {
    return str;
  }
};

export const diffObjects = (obj1, obj2, path = '') => {
  const isObject = (value) => value !== null && typeof value === 'object';
  const differences = [];

  // Arrays diffs
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    const maxLength = Math.max(obj1.length, obj2.length);
    for (let i = 0; i < maxLength; i++) {
      const currentPath = `${path}[${i}]`;

      if (i >= obj1.length) {
        differences.push({
          type: 'added',
          path: currentPath,
          newValue: obj2[i]
        });
        continue;
      }

      if (i >= obj2.length) {
        differences.push({
          type: 'removed',
          path: currentPath,
          oldValue: obj1[i]
        });
        continue;
      }

      differences.push(
        ...diffObjects(obj1[i], obj2[i], currentPath)
      );
    }
    return differences;
  }

  // Objects diffs
  if (isObject(obj1) && isObject(obj2)) {
    const allKeys = new Set([
      ...Object.keys(obj1),
      ...Object.keys(obj2)
    ]);

    for (const key of allKeys) {
      const currentPath = path ? `${path}.${key}` : key;

      const val1 = obj1[key];
      const val2 = obj2[key];

      if (!(key in obj1)) {
        differences.push({
          type: val2 !== undefined && val2 !== null && val2 !== '' ? 'added' : 'removed',
          path: currentPath,
          newValue: val2
        });
        continue;
      }

      if (!(key in obj2)) {
        differences.push({
          type: 'removed',
          path: currentPath,
          oldValue: val1
        });
        continue;
      }

      if ((isObject(val1) && isObject(val2)) || (Array.isArray(val1) && Array.isArray(val2))) {
        differences.push(
          ...diffObjects(val1, val2, currentPath)
        );
        continue;
      }

      if (val1 !== val2) {
        differences.push({
          type: 'changed',
          path: currentPath,
          oldValue: val1,
          newValue: val2
        });
      }
    }
    return differences;
  }

  // Primitive values diffs
  if (obj1 !== obj2) {
    differences.push({
      type: 'changed',
      path,
      oldValue: obj1,
      newValue: obj2
    });
  }

  return differences;
}
